/**
 * Motor del comparador internacional (ADR 001).
 *
 * Vivía completo dentro de `reference-price.liquid`, que es la ficha de
 * producto. Al pedirle el veredicto también al pulso del home había dos
 * caminos: copiar 150 líneas de cálculo de precios, o sacarlas a un lugar
 * común. Se sacaron. Dos copias del mismo cálculo se separan en la primera
 * corrección y la tienda acaba diciendo "🔥 42%" en el home y "👍 12%" en la
 * ficha de la misma carta, que es peor que no decir nada.
 *
 * Aquí solo vive el CÁLCULO. Cada consumidor pinta lo suyo: la ficha una tabla
 * con las dos fuentes, el pulso un chip. El motor no toca el DOM.
 *
 *   vaultReferencia.resolver(sku, centavos) → Promise<veredicto | null>
 *
 * `null` significa "no hay nada honesto que decir" —no es Near Mint, Scryfall
 * no conoce la carta, no hay precio en dólares, está bajo el mínimo— y quien
 * llama simplemente no pinta. Preferimos no informar a desinformar.
 *
 * Se carga con `defer`, así que al ejecutarse los consumidores ya corrieron.
 * Por eso el contrato es una cola y no una llamada directa:
 *
 *   (window.vaultReferenciaCola = window.vaultReferenciaCola || []).push(fn);
 *
 * La configuración llega por `window.vaultReferenciaConfig`, que escribe
 * `vault-referencia-motor.liquid` desde los ajustes del tema.
 */
(function () {
  if (window.vaultReferencia) return;

  var MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

  /* Misma llave que usaba la ficha: la caché ya calentada por una visita
     anterior sigue sirviendo, no se tira al mudar el código de archivo. */
  var PREFIJO = 'vault-ref:1:';
  var HOY = new Date().toISOString().slice(0, 10);

  var SIE = 'https://www.banxico.org.mx/SieAPIRest/service/v1/series/';

  var fxPromise = null;
  var cardCache = {};

  function ajustes() {
    var c = window.vaultReferenciaConfig || {};
    return {
      token: c.token || '',
      minUsd: Number(c.minUsd) || 1,
      envio: Number(c.envio) || 0,
      envioEur: Number(c.envioEur) || 0,
      importPct: Number(c.importPct) || 0,
      divergenciaEur: Number(c.divergenciaEur) || 60,
      corteBueno: Number(c.corteBueno) || 70,
      corteJusto: Number(c.corteJusto) || 90,
      espera: c.espera || '',
    };
  }

  /* Caché entre fichas. `fxPromise` y `cardCache` viven una sola carga de
     página, así que sin esto el módulo volvía a pedir el FIX y la carta en CADA
     ficha que abres — y era la causa real de que apareciera tarde, no la
     velocidad de las APIs (Banxico responde en ~130 ms).

     La llave lleva la fecha local: tanto el FIX de Banxico como los precios de
     Scryfall se mueven una vez al día, así que la entrada caduca sola al cambiar
     el día. Sin TTL que calcular ni invalidar.

     Todo va envuelto en try/catch: en modo privado o con cookies bloqueadas,
     `localStorage` no falla al leer — TIRA al construirse el acceso. Sin el
     catch, el módulo entero se cae en esos navegadores. */
  function cacheGet(key) {
    try {
      var raw = localStorage.getItem(PREFIJO + key);
      if (!raw) return null;
      var o = JSON.parse(raw);
      return o && o.d === HOY ? o.v : null;
    } catch (e) {
      return null;
    }
  }

  function cacheSet(key, val) {
    try {
      localStorage.setItem(PREFIJO + key, JSON.stringify({ d: HOY, v: val }));
    } catch (e) {
      /* Cuota llena o storage bloqueado: seguimos sin caché, nunca rotos. */
    }
  }

  /* Barremos lo de días pasados una vez por carga. Sin esto, cada día de
     navegación deja su propia tanda de cartas muertas hasta llenar la cuota, y a
     partir de ahí `cacheSet` falla siempre y en silencio. */
  function cachePurga() {
    try {
      for (var i = localStorage.length - 1; i >= 0; i--) {
        var k = localStorage.key(i);
        if (!k || k.indexOf(PREFIJO) !== 0) continue;
        var o = JSON.parse(localStorage.getItem(k) || 'null');
        if (!o || o.d !== HOY) localStorage.removeItem(k);
      }
    } catch (e) {
      /* idem */
    }
  }
  cachePurga();

  /* El token va en el QUERY, no en el header Bmx-Token — y es obligatorio que
     sea así: con el header la petición deja de ser "simple", dispara un
     preflight OPTIONS, y Banxico responde ese OPTIONS *sin ningún header CORS* →
     el navegador la bloquea con "Failed to fetch". Como query param no hay
     preflight y el GET sí trae Access-Control-Allow-Origin: *. (El token ya es
     público en este HTML, así que no se pierde nada.)

     SF63528 = FIX USD/MXN. SF46410 = euro respecto al peso. */
  function fetchFx() {
    if (fxPromise) return fxPromise;

    var guardado = cacheGet('fx');
    if (guardado) {
      fxPromise = Promise.resolve(guardado);
      return fxPromise;
    }

    var url = SIE + 'SF63528,SF46410/datos/oportuno?token=' + encodeURIComponent(ajustes().token);

    fxPromise = fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var series = j && j.bmx && j.bmx.series;
        if (!series) return null;
        var out = { fecha: '' };
        series.forEach(function (s) {
          var d = s.datos && s.datos[0];
          if (!d) return;
          var v = parseFloat(d.dato);
          if (!isFinite(v) || v <= 0) return;
          if (s.idSerie === 'SF63528') {
            out.usd = v;
            var p = String(d.fecha).split('/');
            if (p.length === 3) out.fecha = parseInt(p[0], 10) + '-' + MESES[parseInt(p[1], 10) - 1];
          } else if (s.idSerie === 'SF46410') {
            out.eur = v;
          }
        });
        if (!out.usd) return null;
        cacheSet('fx', out);
        return out;
      })
      .catch(function () { return null; });
    return fxPromise;
  }

  /* FIN-434//434-EN-NF-1 → { set, number, foil, nm }
     El último segmento es la condición: 1=NM, 2=LP, 3=MP, 4=HP, 5=Damaged. */
  function parseSku(sku) {
    if (!sku) return null;
    var parts = String(sku).toUpperCase().replace(/\/\/[^-]*-/, '-').split('-');
    if (parts.length < 5) return null;
    if (!parts[0] || !parts[1]) return null;
    return {
      set: parts[0].toLowerCase(),
      number: parts[1],
      foil: parts[3] === 'FO',
      nm: parts[4] === '1',
    };
  }

  function fetchCard(ref) {
    var key = ref.set + '/' + ref.number;
    if (cardCache[key]) return cardCache[key];

    var guardado = cacheGet(key);
    if (guardado) {
      cardCache[key] = Promise.resolve(guardado);
      return cardCache[key];
    }

    cardCache[key] = fetch('https://api.scryfall.com/cards/' + encodeURIComponent(ref.set) + '/' + encodeURIComponent(ref.number))
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        /* Guardamos SOLO los precios, no la carta entera: la respuesta de
           Scryfall son ~5 KB de imágenes, rulings y legalidades que no usamos, y
           a ese tamaño unas cuantas decenas de fichas llenan la cuota. */
        if (!j || !j.prices) return null;
        var slim = { prices: j.prices };
        cacheSet(key, slim);
        return slim;
      })
      .catch(function () { return null; });
    return cardCache[key];
  }

  /**
   * Trae muchas cartas de un golpe. Devuelve una promesa que se cumple cuando
   * todas están en caché; después `resolver` no toca la red.
   *
   * Scryfall tiene un endpoint por lote —hasta 75 identificadores en un POST— y
   * usarlo es la diferencia entre 1 petición y 20. El pulso del home necesita
   * mirar unas 24 cartas para juntar 10 con veredicto (medido: 57% resuelven);
   * de una en una eso son 16-24 viajes espaciados, unos 4 segundos y un llenado
   * a tirones. Por lote es un viaje de ~400 ms y un solo repintado.
   *
   * La ficha de producto no lo usa: ahí solo hay una carta.
   */
  function precargar(skus) {
    var pendientes = [];
    var apuntados = {};

    skus.forEach(function (sku) {
      var ref = parseSku(sku);
      if (!ref || !ref.nm) return;
      var key = ref.set + '/' + ref.number;
      if (apuntados[key] || cardCache[key]) return;

      var guardado = cacheGet(key);
      if (guardado) {
        cardCache[key] = Promise.resolve(guardado);
        return;
      }
      apuntados[key] = true;
      pendientes.push(ref);
    });

    if (!pendientes.length) return Promise.resolve();

    var lotes = [];
    for (var i = 0; i < pendientes.length; i += 75) lotes.push(pendientes.slice(i, i + 75));

    return Promise.all(lotes.map(function (lote) {
      return fetch('https://api.scryfall.com/cards/collection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifiers: lote.map(function (r) {
            return { set: r.set, collector_number: r.number };
          }),
        }),
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          var encontradas = {};
          ((j && j.data) || []).forEach(function (c) {
            if (!c.prices) return;
            var key = c.set + '/' + c.collector_number;
            var slim = { prices: c.prices };
            encontradas[key] = true;
            cacheSet(key, slim);
            cardCache[key] = Promise.resolve(slim);
          });

          /* Las que Scryfall no conoce se marcan SOLO en memoria. Si el fallo
             se guardara en `localStorage`, la ficha de producto de esa carta
             dejaría de intentarlo por el resto del día por culpa de una visita
             al home — y un 404 de Scryfall no siempre es definitivo. */
          lote.forEach(function (r) {
            var key = r.set + '/' + r.number;
            if (!encontradas[key] && !cardCache[key]) cardCache[key] = Promise.resolve(null);
          });
        })
        .catch(function () {
          /* Sin lote se sigue pudiendo: `resolver` cae a la consulta de una en
             una, más lenta pero viva. */
        });
    }));
  }

  function money(n) {
    return '$' + Math.round(n).toLocaleString('es-MX');
  }

  function resolver(sku, centavos) {
    var cfg = ajustes();
    var ref = parseSku(sku);
    if (!ref) return Promise.resolve(null);

    /* Solo Near Mint. Scryfall da UN precio por impresión, que equivale a NM;
       contra una variante jugada la comparación es de peras con manzanas y
       sesgada a nuestro favor (una Damaged se vería como ganga frente a una
       referencia NM). */
    if (!ref.nm) return Promise.resolve(null);

    var nuestro = Number(centavos) / 100;
    if (!isFinite(nuestro) || nuestro <= 0) return Promise.resolve(null);

    return Promise.all([fetchCard(ref), fetchFx()]).then(function (res) {
      var card = res[0];
      var fx = res[1];
      if (!card || !fx || !card.prices) return null;

      var usd = parseFloat(ref.foil ? card.prices.usd_foil : card.prices.usd);
      if (!isFinite(usd) || usd <= 0 || usd < cfg.minUsd) return null;

      var tcg = usd * fx.usd;

      /* Cardmarket (Europa) entra al eje solo si no lo deforma. En sets
         recientes el mercado europeo va muy desfasado — Orcrist marca $302
         contra $1,085 de TCGplayer — y con ese valor dentro, todo lo demás se
         apretuja contra el borde derecho. Cuando diverge demasiado baja a la
         nota al pie: se sigue viendo, pero deja de mandar en la escala. */
      var eur = parseFloat(ref.foil ? card.prices.eur_foil : card.prices.eur);
      var eurMxn = (isFinite(eur) && eur > 0 && fx.eur) ? eur * fx.eur : null;
      var eurEnEje = eurMxn !== null &&
        Math.abs(eurMxn - tcg) / tcg <= (cfg.divergenciaEur / 100);

      /* Costo puesto en México: un punto más, no una banda. Sin configuración de
         envío/importación no se dibuja — preferimos omitirlo a inventarlo. */
      var hayCostoPuesto = cfg.envio > 0 || cfg.importPct > 0;
      var landed = hayCostoPuesto ? tcg + cfg.envio + tcg * (cfg.importPct / 100) : null;

      /* Los dos puntos representan el costo REAL de traerla, no el precio de
         lista: es la alternativa que el comprador de verdad tiene. Traer de
         Europa cuesta más que de EE.UU. (más distancia, sin T-MEC), por eso el
         envío europeo es su propio ajuste — si se deja en cero usa el de EE.UU. */
      var usPoint = hayCostoPuesto ? landed : tcg;
      var eurPoint = eurMxn;
      if (eurMxn !== null && hayCostoPuesto) {
        var shipEur = cfg.envioEur > 0 ? cfg.envioEur : cfg.envio;
        eurPoint = eurMxn + shipEur + eurMxn * (cfg.importPct / 100);
      }

      /* Grado de conveniencia contra la alternativa MÁS BARATA de las dos
         regiones — si Europa sale mejor, esa es la alternativa real.

         Cinco estados SIEMPRE visibles: el chip no es un premio por ganar, es el
         resumen del costo. Si apareciera y desapareciera, el módulo se vería
         roto en vez de informativo. El color carga el significado: verde
         conviene, gris empata, ámbar estamos arriba. */
      var alternativa = eurEnEje ? Math.min(usPoint, eurPoint) : usPoint;
      var ratio = hayCostoPuesto ? (nuestro / alternativa) * 100 : null;
      var ahorro = ratio !== null ? Math.round(100 - ratio) : 0;

      /* Nada de promesas de entrega que no podamos sostener. El módulo NO dice
         cuándo llega lo nuestro —eso depende de envío y de si aplica express—
         solo dice lo que el comprador se ahorra con certeza: el trámite de
         aduana. El tiempo de importación es un dato de operación, así que viaja
         como ajuste: si está vacío, no se afirma nada. */
      var esperaSuf = cfg.espera ? ' ni ' + cfg.espera + ' de espera' : '';
      var esperaFrase = cfg.espera ? ', pero tarda ' + cfg.espera : '';

      /* `txt` es la frase completa de la ficha; `corto` es la misma afirmación
         sin el rodeo, para renglones de lista donde no cabe. Nunca dicen cosas
         distintas: si divergen, una de las dos miente. */
      var grado;
      if (ratio === null) {
        grado = { icon: '🌐', tono: 'neutro', txt: 'Referencia internacional', corto: 'Referencia internacional' };
      } else if (ratio <= cfg.corteBueno) {
        grado = {
          icon: '🔥', tono: 'bueno',
          txt: 'Buena compra — ' + ahorro + '% debajo de traerla',
          corto: ahorro + '% debajo de traerla',
        };
      } else if (ratio <= cfg.corteJusto) {
        grado = {
          icon: '👍', tono: 'bueno',
          txt: 'Precio justo — ' + ahorro + '% debajo de traerla',
          corto: ahorro + '% debajo de traerla',
        };
      } else if (ratio <= 110) {
        grado = {
          icon: '⚖️', tono: 'neutro',
          txt: 'Igual que traerla — aquí sin aduana' + esperaSuf,
          corto: 'Igual que traerla',
        };
      } else {
        /* OJO con la base del porcentaje: "estamos 14% arriba" y "traerla sale
           14% menos" NO son el mismo número — el primero se mide contra la
           alternativa y el segundo contra nuestro precio. */
        var menos = Math.round((1 - alternativa / nuestro) * 100);
        grado = {
          icon: '🇲🇽', tono: 'arriba',
          txt: 'Aquí sin aduana — traerla sale ' + menos + '% menos' + esperaFrase,
          corto: 'Aquí sin aduana',
        };
      }

      var nota = 'TC ' + fx.usd.toFixed(2) + ' — Banxico FIX' + (fx.fecha ? ' ' + fx.fecha : '');
      if (eurMxn !== null && !eurEnEje) {
        nota += ' · Cardmarket (Europa) marca ' + money(eurMxn) + ', fuera de rango';
      }

      return {
        grado: grado,
        hayCostoPuesto: hayCostoPuesto,
        /* La aclaración es condicional: sin costos configurados la columna de la
           derecha es una conversión simple, y decir "envío + importación" sería
           falso. */
        aclara: hayCostoPuesto ? '(envío + importación)' : '',
        usTexto: '$' + usd.toFixed(2) + ' USD',
        usAqui: money(hayCostoPuesto ? usPoint : tcg),
        eurEnEje: eurEnEje,
        eurTexto: eurMxn !== null ? '€' + eur.toFixed(2) : '',
        eurAqui: eurMxn !== null ? money(eurPoint) : '',
        /* Lo que de verdad te costaría traerla: la más barata de las dos rutas.
           Es el número contra el que se calculó el veredicto, no otro. */
        alternativaTexto: money(alternativa),
        nota: nota,
      };
    });
  }

  window.vaultReferencia = { resolver: resolver, precargar: precargar };

  /* Los consumidores se registraron mientras esto se descargaba. Se les da paso
     ahora y se deja una cola falsa que ejecuta al vuelo, por si alguno llega
     tarde (una sección cargada por el editor del tema, por ejemplo). */
  var cola = window.vaultReferenciaCola || [];
  window.vaultReferenciaCola = { push: function (fn) { fn(); } };
  cola.forEach(function (fn) { fn(); });
})();
